import {
  Avatar,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  Image,
  mergeClasses,
} from "@fluentui/react-components";
import {
  Briefcase24Regular,
  ChevronDown20Regular,
  ChevronUp20Regular,
  Comment24Regular,
  DataBarVertical24Regular,
  Dismiss24Regular,
  Document24Regular,
  Home24Regular,
  Navigation20Regular,
  People24Regular,
  QuestionCircle24Regular,
  Settings24Regular,
} from "@fluentui/react-icons";
import { Constants, ConstantValues } from "common/constants";
import { UserProfileMenu } from "components/userProfileMenu/UserProfileMenu";
import { API_BASE, PROJECT_IQ_URL } from "config";
import { useIsMobile } from "hooks/useIsMobile";
import { WorkspaceIcon } from "icons/WorkspaceIcon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "store";
import { designTokens } from "theme/customTheme";
import {
  userIsAdminOrPlatformAdmin,
  userIsAiMetricsUser,
  userIsDecoodaUser,
  userIsDiagnosticsUser,
  userIsProjectIqUser,
} from "utils/generalUtils";
import { setCurrentConversation } from "../../redux/conversations";
import { setCurrentMessages } from "../../redux/messages";
import { setCurrentPrompt } from "../../redux/selectedPrompt";
import { setTagsForKey } from "../../redux/tags";
import { setTemporaryChatOpen } from "../../redux/temporaryChat";
import { clearLibraryDocumentsForKey } from "../../redux/workspaceLibraryDocuments";
import { ActionDialog } from "../actionDialog/ActionDialog";
import { HelpChat } from "../helpChat/HelpChat";
import { useStyles } from "./NavigationPanel.styles";

export const NavigationPanel = (): JSX.Element => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const isGenerating = useSelector(
    (state: RootState) => state.aiGeneratedMessageSlice.isGenerating,
  );

  const isMobile = useIsMobile();
  const [showLogoutDialog, setShowLogoutDialog] = useState<boolean>(false);
  const [showIssueDialog, setShowIssueDialog] = useState<boolean>(false);
  const [showHelpChat, setShowHelpChat] = useState<boolean>(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState<boolean>(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState<boolean>(false);
  const [isDrawerClosing, setIsDrawerClosing] = useState<boolean>(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const userMenuAreaRef = useRef<HTMLDivElement>(null);
  const navItemsRef = useRef<HTMLDivElement>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const drawerCloseTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      clearTimeout(drawerCloseTimerRef.current);
    };
  }, []);

  const closeMobileDrawer = useCallback((): void => {
    setIsDrawerClosing((prev) => {
      if (prev) return prev;
      setIsUserMenuOpen(false);
      drawerCloseTimerRef.current = setTimeout(() => {
        setIsMobileDrawerOpen(false);
        setIsDrawerClosing(false);
      }, 200);
      return true;
    });
  }, []);

  const handleNavKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!navItemsRef.current) return;
      const buttons = Array.from(
        navItemsRef.current.querySelectorAll<HTMLButtonElement>("button"),
      );
      const currentIndex = buttons.indexOf(e.target as HTMLButtonElement);
      if (currentIndex === -1) return;

      let nextIndex: number | null = null;
      if (e.key === "ArrowDown") {
        nextIndex = currentIndex < buttons.length - 1 ? currentIndex + 1 : 0;
      } else if (e.key === "ArrowUp") {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;
      } else if (e.key === "Home") {
        nextIndex = 0;
      } else if (e.key === "End") {
        nextIndex = buttons.length - 1;
      }

      if (nextIndex !== null) {
        e.preventDefault();
        buttons[nextIndex].focus();
      }
    },
    [],
  );

  const handleLogout = (): void => {
    setShowLogoutDialog(true);
  };

  const clearLastPage = useCallback((): void => {
    if (!currentUser?.id) return;
    sessionStorage.removeItem(`lastPage:${currentUser.id}`);
  }, [currentUser?.id]);

  useEffect(() => {
    if (!currentUser?.id) return;

    const key = `lastPage:${currentUser.id}`;
    const lastPage = sessionStorage.getItem(key);
    const currentPath = window.location.pathname;

    if (
      lastPage &&
      (currentPath === "/" || currentPath === ConstantValues.EMPTY_STRING)
    ) {
      clearLastPage();
      navigate(lastPage, { replace: true });
    }
  }, [currentUser?.id]);

  useEffect(() => {
    if (!isUserMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        userMenuAreaRef.current &&
        !userMenuAreaRef.current.contains(e.target as Node)
      ) {
        setIsUserMenuOpen(false);
        menuTriggerRef.current?.focus();
      }
    };
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsUserMenuOpen(false);
        menuTriggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isUserMenuOpen]);

  const getLogoutActionDialog = (): JSX.Element => (
    <ActionDialog
      isVisible={showLogoutDialog}
      title={"Log Out Alert"}
      message={"Are you sure you want to log out?"}
      confirmationMessage={"Yes"}
      dismissMessage={"No"}
      spinnerMessage={"Logging out..."}
      onConfirmation={onLogoutConfirmed}
      onDismiss={(): void => setShowLogoutDialog(false)}
    />
  );

  const onLogoutConfirmed = async () => {
    clearLastPage();
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = `${API_BASE}/auth/logout/`;
  };

  const resetStatesAfterNavigation = useCallback((): void => {
    dispatch(
      setCurrentPrompt({
        key: ConstantValues.CHAT,
        prompt: { description: ConstantValues.EMPTY_STRING },
      }),
    );
    dispatch(setTemporaryChatOpen(false));
    dispatch(setCurrentMessages([]));
    dispatch(setCurrentConversation(null));
    dispatch(setTagsForKey({ key: ConstantValues.CHAT, tags: [] }));
    dispatch(clearLibraryDocumentsForKey(ConstantValues.CHAT));
  }, [dispatch]);

  const handleAdminClick = useCallback((): void => {
    resetStatesAfterNavigation();
    navigate("/admin");
    clearLastPage();
  }, [resetStatesAfterNavigation, navigate, clearLastPage]);

  const handleWorkspacesClick = useCallback((): void => {
    resetStatesAfterNavigation();
    navigate("/workspaces");
    clearLastPage();
  }, [resetStatesAfterNavigation, navigate, clearLastPage]);

  const isButtonSelected = (path: string): boolean => {
    return (
      (path !== "/" && location.pathname.includes(path)) ||
      location.pathname === path
    );
  };

  const getIconClass = (path: string): string => {
    return isButtonSelected(path) ? styles.iconStyleSelected : styles.iconStyle;
  };

  const handleNewChatClick = useCallback((): void => {
    resetStatesAfterNavigation();
    navigate("/");
    clearLastPage();
  }, [resetStatesAfterNavigation, navigate, clearLastPage]);

  const handleMyChatsClick = useCallback((): void => {
    resetStatesAfterNavigation();
    navigate("/chats");
    clearLastPage();
  }, [resetStatesAfterNavigation, navigate, clearLastPage]);

  const handleHelpClick = useCallback(() => {
    setShowHelpChat(true);
    if (isMobile) {
      closeMobileDrawer();
    }
  }, [isMobile, closeMobileDrawer]);

  const navItems = useMemo(
    () => [
      {
        path: "/",
        label: "Home",
        onClick: handleNewChatClick,
        Icon: Home24Regular,
      },
      {
        path: "/chats",
        label: "My Chats",
        onClick: handleMyChatsClick,
        Icon: Comment24Regular,
      },
      {
        path: "/workspaces",
        label: "Workspaces",
        onClick: handleWorkspacesClick,
        Icon: WorkspaceIcon,
      },
      ...(userIsDiagnosticsUser(currentUser)
        ? [
            {
              path: "/diagnostics",
              label: "AI Interviews",
              Icon: People24Regular,
            },
          ]
        : []),
      ...(userIsDecoodaUser(currentUser)
        ? [
            {
              path: "/ai-analytics",
              label: "AI Analytics",
              Icon: Document24Regular,
            },
          ]
        : []),
      ...(userIsAiMetricsUser(currentUser)
        ? [
            {
              path: "/ai-metrics",
              label: "AI Metrics",
              Icon: DataBarVertical24Regular,
            },
          ]
        : []),
      {
        path: "help-modal", // Use a non-route path to prevent navigation
        label: "Help",
        onClick: handleHelpClick,
        Icon: QuestionCircle24Regular,
      },
      ...(userIsAdminOrPlatformAdmin(currentUser)
        ? [
            {
              path: "/admin",
              label: "Admin",
              onClick: handleAdminClick,
              Icon: Settings24Regular,
            },
          ]
        : []),
    ],
    [
      currentUser,
      handleNewChatClick,
      handleMyChatsClick,
      handleWorkspacesClick,
      handleAdminClick,
      handleHelpClick,
    ],
  );

  const handleMobileNavClick = (onClick?: () => void, path?: string) => {
    if (onClick) {
      onClick();
    } else if (path) {
      navigate(path);
    }
    closeMobileDrawer();
  };

  const renderUserMenuFooter: (mobile: boolean) => JSX.Element = (mobile) => (
    <div
      ref={userMenuAreaRef}
      className={mobile ? styles.mobileUserMenuArea : styles.userMenuArea}
    >
      {isUserMenuOpen && (
        <div
          className={
            mobile ? styles.mobileUserMenuPopup : styles.desktopUserMenuPopup
          }
        >
          <UserProfileMenu
            onLogout={() => {
              setIsUserMenuOpen(false);
              handleLogout();
              if (mobile) closeMobileDrawer();
            }}
            onReportAnIssue={() => {
              setIsUserMenuOpen(false);
              setShowIssueDialog(true);
              if (mobile) closeMobileDrawer();
            }}
            onAcceptableUsePolicy={() => {
              setIsUserMenuOpen(false);
              menuTriggerRef.current?.focus();
              window.open(
                "/acceptable-use-policy",
                "_blank",
                "noopener,noreferrer",
              );
              if (mobile) closeMobileDrawer();
            }}
            currentUser={currentUser}
          />
        </div>
      )}
      <div
        className={
          isUserMenuOpen
            ? styles.dividerWrapper
            : styles.dividerWrapperMenuClosed
        }
      >
        <Divider appearance="subtle" className={styles.divider} />
      </div>

      <div className={styles.footerButtonsContainer}>
        <Button
          ref={menuTriggerRef}
          appearance="transparent"
          className={!mobile ? styles.collapsedButtonIcon : styles.button}
          onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
          aria-expanded={isUserMenuOpen}
          aria-haspopup="true"
          aria-label="User profile menu"
        >
          <Avatar
            name={currentUser.displayName}
            size={designTokens.avatarSize.md}
            initials={{
              style: {
                backgroundColor: designTokens.accent[500],
                color: designTokens.neutral[50],
              },
            }}
          />
          <div
            className={mergeClasses(
              styles.footerUserInfo,
              !mobile && styles.navItemTextCollapsed,
            )}
          >
            <span className={styles.footerUserName}>
              {currentUser.displayName}
            </span>
          </div>
          <span
            className={mergeClasses(
              styles.chevronIcon,
              !mobile && styles.navItemTextCollapsed,
            )}
          >
            {isUserMenuOpen ? <ChevronDown20Regular /> : <ChevronUp20Regular />}
          </span>
        </Button>
      </div>
    </div>
  );

  const issueDialog: JSX.Element = (
    <ActionDialog
      isVisible={showIssueDialog}
      title="Report an Issue"
      message={`Clicking "Continue" will open an email to our support team. For faster resolution, please describe what happened in detail, attach screenshots if possible, and include any error messages you received.`}
      confirmationMessage="Continue"
      dismissMessage="Cancel"
      onConfirmation={() => {
        window.open(Constants.ISSUE_URL_MAILTO, "_blank");
        setShowIssueDialog(false);
      }}
      onDismiss={() => setShowIssueDialog(false)}
    />
  );

  if (isMobile) {
    return (
      <>
        {showLogoutDialog && getLogoutActionDialog()}
        <Button
          className={styles.hamburgerButton}
          icon={<Navigation20Regular />}
          onClick={() => setIsMobileDrawerOpen(true)}
          aria-label="Open navigation menu"
          aria-expanded={isMobileDrawerOpen}
        />
        <Drawer
          type="overlay"
          position="start"
          open={isMobileDrawerOpen}
          aria-label="Navigation menu"
          onOpenChange={(_, { open }) => {
            if (open) {
              setIsMobileDrawerOpen(true);
            } else {
              closeMobileDrawer();
            }
          }}
          className={styles.mobileDrawer}
        >
          <div
            className={mergeClasses(
              styles.mobileDrawerBody,
              isDrawerClosing && styles.mobileDrawerBodyClosing,
            )}
          >
            <div className={styles.mobileDrawerHeader}>
              <div className={styles.mobileDrawerLogoRow}>
                <Image
                  className={styles.logoImage}
                  src="/images/logoheaderfinal.png"
                  alt="NH AIMS Logo"
                />
              </div>
              <Button
                appearance="subtle"
                className={styles.mobileDrawerCloseButton}
                icon={<Dismiss24Regular />}
                onClick={() => closeMobileDrawer()}
                aria-label="Close navigation"
              />
            </div>

            <DrawerBody className={styles.mobileDrawerBodyInner}>
              <div className={styles.mobileNavContent}>
                <div
                  ref={navItemsRef}
                  role="navigation"
                  aria-label="Main navigation"
                  className={styles.mobileNavItemsContainer}
                  onKeyDown={handleNavKeyDown}
                >
                  {navItems.map(({ path, label, Icon, onClick }) => (
                    <Button
                      key={path}
                      className={
                        isButtonSelected(path)
                          ? styles.buttonSelected
                          : styles.button
                      }
                      disabled={isGenerating}
                      onClick={() => handleMobileNavClick(onClick, path)}
                      icon={<Icon className={getIconClass(path)} />}
                      size="large"
                      aria-label={label}
                    >
                      <span className={styles.navItemText}>{label}</span>
                    </Button>
                  ))}
                </div>
                {renderUserMenuFooter(true)}
              </div>
            </DrawerBody>
          </div>
        </Drawer>
        {issueDialog}
        <HelpChat
          isOpen={showHelpChat}
          onDismiss={() => setShowHelpChat(false)}
        />
      </>
    );
  }

  return (
    <>
      {showLogoutDialog && getLogoutActionDialog()}
      <HelpChat
        isOpen={showHelpChat}
        onDismiss={() => setShowHelpChat(false)}
      />
      <aside ref={drawerRef} className={styles.main}>
        <div className={styles.drawerContainer}>
          <div
            className={mergeClasses(
              styles.headerContainer,
              styles.headerContainerCollapsed,
            )}
          >
            <Image
              className={styles.logoImage}
              src="/images/logoheaderfinal.png"
              alt="NH AIMS Logo"
            />
          </div>
          <div className={styles.navContent}>
            <div
              ref={navItemsRef}
              role="navigation"
              aria-label="Main navigation"
              className={styles.navItemsContainer}
              onKeyDown={handleNavKeyDown}
            >
              {navItems.map(({ path, label, Icon, onClick }) => (
                <Button
                  key={path}
                  className={styles.collapsedButton}
                  disabled={isGenerating}
                  onClick={() => (onClick ? onClick() : navigate(path))}
                  icon={<Icon className={styles.iconStyleSelected} />}
                  size="large"
                  aria-label={label}
                >
                  <span className={styles.collapsedNavLabel}>{label}</span>
                </Button>
              ))}
            </div>
            {renderUserMenuFooter(false)}
          </div>
        </div>
        {issueDialog}
      </aside>
    </>
  );
};
